import {Component, OnInit} from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import * as _ from 'lodash';
import { forkJoin } from 'rxjs';

import { ReportsService } from '../reports.service';
import { QuicksightReportsRolesService } from '../quicksight-reports-roles.service';
import { FederatedIdentityGroupService } from '../../federated-identity-groups/federated-identity-group.service';
import { Report } from '../report';

@Component({
    selector: 'app-new-report-modal',
    templateUrl: './new-report.component.html',
    styleUrls: ['./new-report.component.scss'],
    standalone: false
})
export class NewReportModalComponent implements OnInit {
  afterCreate = {
    next: (v) => {
      this.data = v.data;
      this.added = true;
      this.modalRef.hide();
    },
    error: () => {
      this.modalRef.hide();
    }
  };
  afterUpdate = {
    next: () => {
      this.updated = true;
      this.modalRef.hide();
    },
    error: () => {
      this.modalRef.hide();
    }
  };
  afterDelete = {
    next: () => {
      this.deleted = true;
      this.modalRef.hide();
    },
    error: () => {
      this.modalRef.hide();
    }
  };

  report: Report;
  editingReport: boolean;
  added = false;
  deleted = false;
  updated = false;
  data = {}
  roles: any[] = [];
  selectedRoleIds: number[] = [];
  loadingRoles = false;

  constructor(
    public modalRef: BsModalRef,
    private ReportsService: ReportsService,
    private quicksightReportsRolesService: QuicksightReportsRolesService,
    private federatedIdentityGroupService: FederatedIdentityGroupService
  ) {}

  ngOnInit() {
      if (!this.report) {
        this.report = this.blankReport();
      }

      this.loadingRoles = true;

      // Fetch roles
      this.federatedIdentityGroupService.getRoles().subscribe({
        next: (response) => {
          // Filter to only show dispatcher and care_coordinator roles
          const allRoles = response.roles || [];
          this.roles = allRoles.filter(role => {
            const roleCode = role.code?.toLowerCase() || '';
            return roleCode === 'dispatcher' || roleCode === 'care_coordinator';
          });

          // If editing, fetch existing role associations
          if (this.report.id) {
            this.quicksightReportsRolesService.list({ quicksight_report_id: parseInt(this.report.id) }).subscribe({
              next: (roleAssociations) => {
                this.selectedRoleIds = roleAssociations.map(ra => ra.role_id);
                this.loadingRoles = false;
              },
              error: () => {
                this.loadingRoles = false;
              }
            });
          } else {
            this.loadingRoles = false;
          }
        },
        error: () => {
          this.loadingRoles = false;
        }
      });
  }

  addUpdateReport() {
    if (this.report.id) {
      // Update existing report
      this.ReportsService.update(this.report.id, this.report).subscribe({
        next: () => {
          this.syncRoleAssociations(parseInt(this.report.id));
        },
        error: () => {
          this.modalRef.hide();
        }
      });
    } else {
      // Create new report
      this.ReportsService.create(this.report).subscribe({
        next: (response) => {
          this.data = response.data || response;
          const reportId = response.quicksight_report?.id;
          if (reportId) {
            this.syncRoleAssociations(reportId);
          } else {
            this.added = true;
            this.modalRef.hide();
          }
        },
        error: () => {
          this.modalRef.hide();
        }
      });
    }
  }

  private syncRoleAssociations(reportId: number) {
    // Fetch existing role associations
    this.quicksightReportsRolesService.list({ quicksight_report_id: reportId }).subscribe({
      next: (existingAssociations) => {
        const existingRoleIds = existingAssociations.map(ra => ra.role_id);

        // Determine which roles to add and which to remove
        const rolesToAdd = this.selectedRoleIds.filter(roleId => !existingRoleIds.includes(roleId));
        const rolesToRemove = existingAssociations.filter(ra => !this.selectedRoleIds.includes(ra.role_id));

        const operations = [];

        // Create new associations
        rolesToAdd.forEach(roleId => {
          operations.push(
            this.quicksightReportsRolesService.create({
              role_id: roleId,
              quicksight_report_id: reportId
            })
          );
        });

        // Delete removed associations
        rolesToRemove.forEach(association => {
          operations.push(
            this.quicksightReportsRolesService.delete(association.id)
          );
        });

        // Execute all operations
        if (operations.length > 0) {
          forkJoin(operations).subscribe({
            next: () => {
              this.finalizeUpdate();
            },
            error: () => {
              this.finalizeUpdate();
            }
          });
        } else {
          this.finalizeUpdate();
        }
      },
      error: () => {
        this.finalizeUpdate();
      }
    });
  }

  private finalizeUpdate() {
    if (this.report.id) {
      this.updated = true;
    } else {
      this.added = true;
    }
    this.modalRef.hide();
  }

  deleteReport(){
    if (this.report.id){
      this.ReportsService.delete(this.report.id).subscribe(this.afterDelete);
    }
  }

  toggleRoleSelection(roleId: number) {
    const index = this.selectedRoleIds.indexOf(roleId);
    if (index > -1) {
      this.selectedRoleIds.splice(index, 1);
    } else {
      this.selectedRoleIds.push(roleId);
    }
  }

  isRoleSelected(roleId: number): boolean {
    return this.selectedRoleIds.includes(roleId);
  }

  private blankReport(): Report {
    return <Report>{
      uuid:'',
      report_name:''
    };
  }
}
